// AdminTrainingDataPage — Phase 16 Gate 5 — superadmin training-mirror browse + export.

import { useEffect, useState } from "react";
import { Download, RefreshCw } from "lucide-react";
import { admin } from "@/api/endpoints";
import { serverDetail } from "@/api/client";

export default function AdminTrainingDataPage() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState({ tenant_hash: "", backend: "", outcome: "" });
    const [exportRange, setExportRange] = useState({ start: "", end: "" });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState(null);

    async function refresh() {
        setBusy(true);
        setErr(null);
        try {
            const params = {};
            Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
            const data = await admin.listTrainingMirror(params);
            setItems(data.items || []);
        } catch (e) {
            setErr(serverDetail(e));
        } finally {
            setBusy(false);
        }
    }

    // Phase 14.8 W5 — `refresh` is recreated on every render; effect
    // intentionally fires once on mount only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refresh(); }, []);

    function exportZip() {
        const url = admin.trainingMirrorExportUrl(exportRange.start, exportRange.end);
        window.location.href = url;
    }

    return (
        <div data-testid="admin-training-data-page" style={{ padding: 24 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                    <h1 style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 24, color: "var(--ux-text-1)" }}>
                        Training Data Mirror
                    </h1>
                    <p style={{ color: "var(--ux-text-3)", fontSize: 13 }}>
                        Per-run mirror entries. Tenant identity is sha256-hashed; no PII is stored.
                    </p>
                </div>
                <button type="button" className="btn-secondary" onClick={refresh} disabled={busy} data-testid="admin-training-refresh">
                    <RefreshCw size={14} /> Refresh
                </button>
            </div>

            {err && <div className="banner banner--danger" data-testid="admin-training-error">{err}</div>}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr) auto", gap: 8, marginBottom: 16, alignItems: "end" }}>
                <input
                    placeholder="tenant_hash filter"
                    value={filter.tenant_hash}
                    onChange={(e) => setFilter({ ...filter, tenant_hash: e.target.value })}
                    data-testid="admin-training-filter-tenant"
                    style={inputStyle()}
                />
                <input
                    placeholder="backend (kinematic / jsbsim_hybrid)"
                    value={filter.backend}
                    onChange={(e) => setFilter({ ...filter, backend: e.target.value })}
                    data-testid="admin-training-filter-backend"
                    style={inputStyle()}
                />
                <input
                    placeholder="outcome (intercept / miss …)"
                    value={filter.outcome}
                    onChange={(e) => setFilter({ ...filter, outcome: e.target.value })}
                    data-testid="admin-training-filter-outcome"
                    style={inputStyle()}
                />
                <button type="button" className="btn-primary" onClick={refresh} data-testid="admin-training-apply-filters">
                    Apply
                </button>
            </div>

            <div style={{
                border: "1px solid var(--ux-border-1)",
                borderRadius: 8,
                padding: 16,
                marginBottom: 16,
                background: "var(--ux-bg-1)",
                display: "grid",
                gridTemplateColumns: "1fr 1fr auto",
                gap: 8,
                alignItems: "end",
            }}>
                <div>
                    <label style={{ color: "var(--ux-text-3)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Export start (ISO)
                    </label>
                    <input
                        placeholder="2026-01-01T00:00:00Z"
                        value={exportRange.start}
                        onChange={(e) => setExportRange({ ...exportRange, start: e.target.value })}
                        data-testid="admin-training-export-start"
                        style={inputStyle()}
                    />
                </div>
                <div>
                    <label style={{ color: "var(--ux-text-3)", fontSize: 11, letterSpacing: "0.06em", textTransform: "uppercase" }}>
                        Export end (ISO)
                    </label>
                    <input
                        placeholder="2026-12-31T23:59:59Z"
                        value={exportRange.end}
                        onChange={(e) => setExportRange({ ...exportRange, end: e.target.value })}
                        data-testid="admin-training-export-end"
                        style={inputStyle()}
                    />
                </div>
                <button type="button" className="btn-primary" onClick={exportZip} data-testid="admin-training-export">
                    <Download size={14} /> Export ZIP
                </button>
            </div>

            <div style={{ overflowX: "auto", border: "1px solid var(--ux-border-1)", borderRadius: 8 }}>
                <table style={{ width: "100%", borderCollapse: "collapse", color: "var(--ux-text-1)", fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: "var(--ux-bg-1)", textAlign: "left" }}>
                            <th style={th()}>Created</th>
                            <th style={th()}>tenant_hash</th>
                            <th style={th()}>Backend</th>
                            <th style={th()}>Outcome</th>
                            <th style={th()}>Miss (m)</th>
                            <th style={th()}>Files</th>
                        </tr>
                    </thead>
                    <tbody>
                        {items.length === 0 ? (
                            <tr><td colSpan={6} style={{ padding: 24, textAlign: "center", color: "var(--ux-text-3)" }}>
                                No mirror entries match these filters.
                            </td></tr>
                        ) : items.map((it) => (
                            <tr key={it.run_id} style={{ borderTop: "1px solid var(--ux-border-1)" }}
                                data-testid={`admin-training-row-${it.run_id}`}>
                                <td style={td()}>{it.created_at?.slice(0, 19).replace("T", " ")}</td>
                                <td style={{ ...td(), fontFamily: "JetBrains Mono, monospace" }}>{it.tenant_hash}</td>
                                <td style={td()}>{it.backend || "—"}</td>
                                <td style={td()}>{it.outcome || "—"}</td>
                                <td style={td()}>{it.miss_distance_m == null ? "—" : Number(it.miss_distance_m).toFixed(2)}</td>
                                <td style={td()}>{(it.files || []).length}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            <div style={{ color: "var(--ux-text-3)", fontSize: 12, marginTop: 8 }}>
                Showing {items.length} entries.
            </div>
        </div>
    );
}

function inputStyle() {
    return {
        background: "var(--ux-bg-0)",
        color: "var(--ux-text-1)",
        border: "1px solid var(--ux-border-2)",
        padding: "8px 10px",
        borderRadius: 6,
        fontSize: 13,
        width: "100%",
    };
}

function th() { return { padding: "10px 12px" }; }
function td() { return { padding: "8px 12px", color: "var(--ux-text-2)" }; }
